import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { Formik, Form } from "formik";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/core/components/ui/input-otp";
import { Stack } from "@/core/components/ui/stack/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import {
  isInvalidPasswordResetCodeError,
  isPasswordResetThrottledError,
  useAuth,
} from "@/core/auth";
import { useBreakpoints } from "@/core/component-hooks/use-breakpoints";
import { cn } from "@/core/utils/utils";
import { ResendCodeButton } from "./resend-code-button";

const resetPasswordCodeSchema = Yup.object().shape({
  verification_code: Yup.string().required("Code is required"),
});

const CODE_ERROR_ID = "reset-code-error";

export default function ResetPasswordCode() {
  const {
    resetPasswordCode,
    forgotPassword,
    forgotPasswordEmail: email,
  } = useAuth();
  const navigate = useNavigate();
  const { isSmallMobile } = useBreakpoints();

  const [autoSubmitEnabled, setAutoSubmitEnabled] = useState(true);
  const [codeError, setCodeError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (values: { verification_code: string }) => {
      try {
        setAutoSubmitEnabled(false);
        await resetPasswordCode({
          ...values,
          email: email || "",
        });
      } catch (error) {
        if (isInvalidPasswordResetCodeError(error)) {
          setCodeError(
            "That code didn't work. Check your latest email, or resend the code.",
          );
        } else if (isPasswordResetThrottledError(error)) {
          setCodeError(error.message);
        } else if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error(
            "Error verifying code, please contact support or try again later",
          );
        }
      }
    },
    [email, resetPasswordCode],
  );

  const handleResendCode = useCallback(
    async (clearCode: () => void) => {
      if (!email) {
        toast.error("No email found, restart the process");
        return navigate("/forgot-password");
      }

      try {
        await forgotPassword({ email });
        clearCode();
        setCodeError(null);
        setAutoSubmitEnabled(true);
        toast.success("New code sent", {
          description: `Check ${email}. Earlier codes no longer work.`,
        });
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to resend code",
        );
      }
    },
    [email, forgotPassword, navigate],
  );

  return (
    <Stack
      direction="row"
      align="center"
      justify="center"
      fullWidth
      className="min-h-[calc(100vh-67px)] overflow-hidden p-6"
    >
      <Card className="w-full max-w-md p-6 pt-10">
        <Formik
          initialValues={{ verification_code: "" }}
          validationSchema={resetPasswordCodeSchema}
          validateOnMount
          onSubmit={async (values, { setSubmitting }) =>
            handleSubmit(values).finally(() => {
              setSubmitting(false);
            })
          }
        >
          {({ values, isSubmitting, isValid, setFieldValue }) => (
            <Form>
              <Stack gap="lg" fullWidth>
                <Typography variant="subtitleW1">Enter Reset Code</Typography>

                <Typography
                  variant="bodyW1"
                  textColor="secondary"
                  className="w-full break-words"
                >
                  {email
                    ? `We sent a 6-digit code to ${email}. Enter it below to continue.`
                    : "Check your email for the 6-digit reset code and enter it below."}
                </Typography>

                <Stack gap="xs" fullWidth>
                  <InputOTP
                    maxLength={6}
                    autoFocus
                    aria-label="Reset code"
                    aria-invalid={codeError ? true : undefined}
                    aria-describedby={CODE_ERROR_ID}
                    value={values.verification_code}
                    onChange={(value) => {
                      setCodeError(null);
                      setFieldValue("verification_code", value);
                      if (autoSubmitEnabled && value.length === 6) {
                        handleSubmit({ ...values, verification_code: value });
                      }
                    }}
                    containerClassName={cn("justify-center mx-auto", {
                      "gap-1": isSmallMobile,
                      "gap-3": !isSmallMobile,
                    })}
                  >
                    {Array.from({ length: 6 }).map((_, index) => (
                      <InputOTPGroup key={index}>
                        <InputOTPSlot
                          index={index}
                          className={cn(
                            "border-input bg-card text-center text-lg shadow-sm",
                            {
                              "border-destructive": codeError,
                              "h-10 w-8": isSmallMobile,
                              "h-12 w-12": !isSmallMobile,
                            },
                          )}
                        />
                      </InputOTPGroup>
                    ))}
                  </InputOTP>

                  <Typography
                    id={CODE_ERROR_ID}
                    aria-live="polite"
                    variant="bodyW2"
                    textColor="negative"
                    className="text-pretty text-center"
                  >
                    {codeError}
                  </Typography>
                </Stack>

                <ResendCodeButton
                  onResend={() =>
                    handleResendCode(() =>
                      setFieldValue("verification_code", ""),
                    )
                  }
                />

                <Stack fullWidth gap="sm">
                  <Button
                    type="submit"
                    size="lg"
                    fullWidth
                    disabled={!isValid}
                    loading={isSubmitting}
                  >
                    Continue
                  </Button>

                  <Button
                    type="button"
                    size="lg"
                    variant="text"
                    fullWidth
                    className="[&_svg]:size-5"
                    leftIcon={<ArrowLeftIcon />}
                    onClick={() => navigate("/forgot-password")}
                  >
                    Back to Reset Password
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </Card>
    </Stack>
  );
}
